import { parsePhoneNumberFromString } from "libphonenumber-js";

/**
 * Normalizes phone numbers to E.164 format for consistent comparison
 * @param {string} phoneNumber - The phone number to normalize
 * @returns {string} - Normalized phone number in E.164 format
 */
export const normalizePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";
  try {
    // Remove all non-digit characters except +
    const cleaned = phoneNumber.replace(/[^\d+]/g, "");
    // Try to parse and get the E.164 format
    const parsed = parsePhoneNumberFromString(cleaned);
    if (parsed) {
      return parsed.number; // Returns in E.164 format (+1234567890)
    }
    // Fallback: if it starts with +, return as is; otherwise add +1 for US numbers
    if (cleaned.startsWith("+")) {
      return cleaned;
    }
    // If it's 10 digits, assume US and add +1
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    }
    // If it's 11 digits and starts with 1, add +
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return `+${cleaned}`;
    }
    return cleaned;
  } catch (error) {
    return phoneNumber;
  }
};

/**
 * Gets a user-friendly label for action details by checking:
 * 1. If it's a forwarding number, returns the channel label
 * 2. If it's an ElevenLabs agent ID, returns the agent name (from org channels)
 * 3. Otherwise, returns the original value
 *
 * ElevenLabs agents are stored per channel (channels with type "elevenlabs" have agents.L),
 * not on organization.agentArray — we derive the agent list from channels.
 *
 * @param {string} actionDetails - The action details value (phone number or agent ID)
 * @param {Array} channels - Array of channels from organization (optional, will fetch from localStorage if not provided)
 * @returns {string} - The label if found, otherwise the original value
 */
export const getActionDetailsLabel = (actionDetails, channels = null) => {
  if (!actionDetails || actionDetails === "N/A") return actionDetails;

  // Get channels from localStorage if not provided
  if (!channels) {
    try {
      channels = JSON.parse(localStorage.getItem("organization"))?.channels?.L || [];
    } catch (error) {
      channels = [];
    }
  }

  // ElevenLabs agent ID: resolve name from agents stored on elevenlabs channels
  const elevenlabsChannels = (channels || []).filter((ch) => ch.M?.type?.S === "elevenlabs");
  for (const ch of elevenlabsChannels) {
    const agents = ch.M?.agents?.L || [];
    for (const item of agents) {
      const agentId = item.M?.id?.S ?? item.M?.agent_id?.S ?? "";
      if (agentId === actionDetails) {
        const agentName = item.M?.name?.S ?? item.M?.agent_name?.S ?? "";
        if (agentName) return agentName;
        return actionDetails;
      }
    }
  }

  // Otherwise, check if it's a forwarding number
  const normalizedActionDetails = normalizePhoneNumber(actionDetails);

  // Find matching channel
  const matchingChannel = channels.find((channel) => {
    if (channel.M?.type?.S !== "forward_number") return false;
    const channelValue = channel.M?.value?.S || "";
    const normalizedChannelValue = normalizePhoneNumber(channelValue);
    return normalizedChannelValue === normalizedActionDetails;
  });

  // If found, return the label; otherwise return the original value
  if (matchingChannel && matchingChannel.M?.label?.S) {
    return matchingChannel.M.label.S;
  }

  return actionDetails;
};
